import { useRef, useState, type FormEvent } from 'react';
import type {
  AttributeDefinitionResponse,
  NewEvidenceItemInput,
  RecordedAssessmentResponse,
  SourceTypeCode,
} from '../../api/types';
import { apiPost, describeError } from '../../api/client';
import { bandFor, EVIDENCE_TYPE_OPTIONS, evidenceTypeLabel, formatCalendarDate, sourceLabel } from './attributeDisplay';
import styles from './AssessmentForm.module.css';

/** The maximum number of evidence items one submission may carry
 * (`api/src/validation/body.ts`'s `MAX_EVIDENCE_ITEMS`) — transcribed
 * here only to disable "Add evidence" at the same limit the API would
 * otherwise refuse; the API re-enforces this regardless. */
const MAX_EVIDENCE_ITEMS = 10;

/** One evidence item being composed, before it exists anywhere. Plain
 * strings throughout, including `restricted` staying a checkbox rather
 * than the wire's `visibility` enum, matching how `rating` is typed text
 * here and parsed only at submission. */
interface DraftEvidenceItem {
  key: number;
  evidenceTypeCode: string;
  description: string;
  occurredOn: string;
  externalReference: string;
  restricted: boolean;
}

/**
 * Recording one assessment (Mission 014 — the first write path in the UI).
 *
 * ---------------------------------------------------------------------
 * WHERE THIS LIVES, AND WHY IT IS NOT A PAGE
 *
 * Inside the expanded attribute row, on the profile of the person being
 * assessed. Both facts the assessment is *about* — who, and which
 * attribute — are already established by where the reader is standing, so
 * neither is a field, and there is no employee picker anywhere in the flow.
 *
 * The alternative, a standalone "record an assessment" destination with a
 * person selector, was rejected: it would introduce exactly the arbitrary
 * employee-browsing surface the product has avoided through four read
 * missions, and it would put the assessor further from the evidence — the
 * composite, its sources, the confidence, the recorded history — that
 * should inform what they type. Here, all of it is on screen above the form.
 *
 * WHAT THE FORM DOES NOT ASK
 *
 * Not the source type. Not the assessor. Not the date. Not the method.
 * A composite weights a manager's rating at 0.35 and a self-rating at 0.10,
 * so "who says this, in what capacity" decides what the number counts for —
 * and every one of those fields is derived by the server from the
 * authenticated caller. The form *states* the perspective it will be
 * recorded under, taken from the API's own `viewerCanAssessAs`, so the
 * assessor knows what they are signing; it never lets them choose it.
 *
 * EVIDENCE IS PART OF THIS SAME WRITE, NOT A SECOND ONE
 * (docs/DOMAIN_DECISIONS.md, "Evidence Attachment Write Contract" EA-1)
 *
 * Zero to ten items, attached in the same `POST` as the rating — there is
 * no standalone evidence endpoint, and this form is the only place
 * evidence can be attached at all (EA-2: not after the fact, not on
 * withdrawal). Kept distinct from rationale, which is offered on the same
 * form for a different reason: rationale is the assessor's own opinion
 * about what the rating means; evidence is independently examinable — a
 * project outcome, a completed work item, a prior result — and is typed,
 * dated and optionally marked restricted rather than freeform.
 *
 * An item's evidence type is offered from the shared vocabulary
 * (`EVIDENCE_TYPE_OPTIONS`), but whether a given type is *permitted for
 * this attribute* is a rule this form does not know and does not
 * pre-check — the same "the API decides, this form does not
 * re-implement it" discipline `sourceType`/`rating` already follow. An
 * unpermitted choice is refused by the API (`evidence_type_not_permitted`)
 * and shown like any other refusal.
 *
 * Marking an item **restricted** does not change who may attach it — EA-3
 * adds no second authorization gate for evidence specifically — it changes
 * who may read it back afterwards: the assessment subject, `admin` and
 * `hr_partner` only (RE-1), not even the assessor who wrote it, unless
 * they also qualify under one of those three.
 *
 * WHAT IT VALIDATES
 *
 * Only what makes the form usable: that a rating has been chosen and is on
 * the 1-20 scale, and that every evidence item has a type and a
 * description (the two fields the API requires). Everything else —
 * whether this source type may assess this attribute, whether a given
 * evidence type is permitted here, whether the caller stands in that
 * relationship at all — is a domain rule, answered by the API, and
 * deliberately not re-implemented here. When the API refuses, its
 * explanation is shown.
 * ---------------------------------------------------------------------
 */

type SubmitState =
  | { status: 'editing' }
  | { status: 'submitting' }
  | { status: 'failed'; message: string }
  | { status: 'recorded'; assessment: RecordedAssessmentResponse };

export function AssessmentForm({
  employeeId,
  employeeName,
  attribute,
  sourceType,
  correcting,
  onRecorded,
  onClose,
}: {
  employeeId: string;
  employeeName: string;
  attribute: AttributeDefinitionResponse;
  /** The perspective the API says this caller stands in. Displayed, never
   * chosen — and re-derived server-side on submission regardless. */
  sourceType: SourceTypeCode;
  /**
   * Present when this is a **correction** rather than a new assessment
   * (Mission 015).
   *
   * The id comes from the record the reader chose, never from anything they
   * typed. A correction still creates a new assessment — that is what a
   * correction *is* here — so the same form does the job, with the wording
   * and the starting value changed to say so.
   */
  correcting?: { assessmentId: string; currentRating: number };
  /** Called after the API confirms. The page re-reads authoritative state;
   * nothing about the new composite is computed here. */
  onRecorded: () => void;
  onClose: () => void;
}) {
  // Prefilled with the value being corrected, so the reader can see what
  // they are changing from and amend a digit rather than retype a judgement
  // from memory. Only ever the rating: prefilling someone's previous
  // rationale would invite them to submit reasoning they did not re-read.
  const [rating, setRating] = useState(correcting ? String(correcting.currentRating) : '');
  const [context, setContext] = useState('');
  const [rationale, setRationale] = useState('');
  const [evidenceItems, setEvidenceItems] = useState<DraftEvidenceItem[]>([]);
  const nextEvidenceKey = useRef(0);
  const [state, setState] = useState<SubmitState>({ status: 'editing' });

  const parsed = Number(rating);
  const ratingIsValid = rating !== '' && Number.isInteger(parsed) && parsed >= 1 && parsed <= 20;
  const evidenceIsValid = evidenceItems.every(
    (item) => item.evidenceTypeCode !== '' && item.description.trim().length > 0
  );
  const submitting = state.status === 'submitting';

  function addEvidenceItem() {
    if (evidenceItems.length >= MAX_EVIDENCE_ITEMS) return;
    setEvidenceItems((items) => [
      ...items,
      {
        key: nextEvidenceKey.current++,
        evidenceTypeCode: '',
        description: '',
        occurredOn: '',
        externalReference: '',
        restricted: false,
      },
    ]);
  }

  function updateEvidenceItem(key: number, patch: Partial<DraftEvidenceItem>) {
    setEvidenceItems((items) => items.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  }

  function removeEvidenceItem(key: number) {
    setEvidenceItems((items) => items.filter((item) => item.key !== key));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    // Guards a double submit: the button is disabled while in flight, but a
    // form can also be submitted by keyboard, and an assessment recorded
    // twice is two real records the composite would count twice.
    if (submitting || !ratingIsValid || !evidenceIsValid) return;

    setState({ status: 'submitting' });
    try {
      const evidence: NewEvidenceItemInput[] = evidenceItems.map((item) => ({
        evidenceTypeCode: item.evidenceTypeCode,
        description: item.description.trim(),
        occurredOn: item.occurredOn || undefined,
        externalReference: item.externalReference.trim() || undefined,
        visibility: item.restricted ? 'restricted' : undefined,
      }));
      const assessment = await apiPost<RecordedAssessmentResponse>(
        `/employees/${employeeId}/attributes/${attribute.id}/assessments`,
        {
          rating: parsed,
          context: context.trim() || undefined,
          rationale: rationale.trim() || undefined,
          supersedesId: correcting?.assessmentId,
          evidence: evidence.length > 0 ? evidence : undefined,
        }
      );
      setState({ status: 'recorded', assessment });
      onRecorded();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  if (state.status === 'recorded') {
    const recorded = state.assessment;
    return (
      <div className={styles.form} role="status">
        <p className={styles.recorded}>
          {recorded.supersedesId ? 'Correction recorded' : 'Recorded'}:{' '}
          <strong>{recorded.rating} out of 20</strong> for {recorded.attributeName}, as{' '}
          {sourceLabel(recorded.sourceType).toLowerCase()}.
        </p>
        {recorded.supersedesId ? (
          <p className={styles.note}>
            This is a new record that replaces the earlier one. The earlier assessment is kept exactly as it was and
            is still listed — it simply no longer counts towards the current figures.
          </p>
        ) : null}
        {recorded.evidence.length > 0 ? (
          <ul className={styles.evidenceSummary}>
            {recorded.evidence.map((item) => (
              <li key={item.id}>
                <strong>{evidenceTypeLabel(item.evidenceTypeCode)}</strong>
                {item.visibility === 'restricted' ? <span className={styles.restrictedBadge}>Restricted</span> : null}
                : {item.description}
                {item.occurredOn ? ` (${formatCalendarDate(item.occurredOn)})` : ''}
              </li>
            ))}
          </ul>
        ) : null}
        <p className={styles.note}>
          What this changes — the composite, the confidence in it, whether sources now disagree, and the trajectory —
          is worked out by the system from every assessment on record, not from this one alone. The figures above have
          been reloaded.
        </p>
        <button type="button" className={styles.secondary} onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  return (
    <form
      className={styles.form}
      onSubmit={handleSubmit}
      aria-label={
        correcting
          ? `Correct your assessment of ${attribute.name}`
          : `Record an assessment of ${attribute.name}`
      }
    >
      {/* Stated, not chosen: the reader needs to know whose perspective this
          will be recorded as, because it is what the rating will count for. */}
      <p className={styles.perspective}>
        {correcting ? 'Correcting your ' : 'Recording '}
        <strong>{sourceLabel(sourceType).toLowerCase()}</strong> perspective on{' '}
        <strong>{attribute.name}</strong> for {employeeName}. The system records who you are; you cannot record this
        as someone else.
      </p>

      {/* Named for what it does. "Edit" would be wrong twice over: nothing
          is edited, and the record being replaced survives. */}
      {correcting ? (
        <p className={styles.perspective}>
          This records a <strong>replacement</strong> for your earlier rating of {correcting.currentRating}. The
          earlier assessment is kept and stays listed; it stops counting towards the current figures.
        </p>
      ) : null}

      <div className={styles.field}>
        <label htmlFor={`rating-${attribute.id}`} className={styles.label}>
          Rating <span className={styles.scale}>1–20</span>
        </label>
        <input
          id={`rating-${attribute.id}`}
          className={styles.rating}
          type="number"
          inputMode="numeric"
          min={1}
          max={20}
          step={1}
          value={rating}
          onChange={(event) => setRating(event.target.value)}
          disabled={submitting}
          required
          aria-describedby={`rating-help-${attribute.id}`}
        />
        <p id={`rating-help-${attribute.id}`} className={styles.help}>
          10–11 is the expected level for a capable employee. 1 means “{attribute.lowEndMeaning}”; 20 means “
          {attribute.highEndMeaning}”.
          {ratingIsValid ? <span className={styles.band}> {parsed} falls in “{bandFor(parsed).label}”.</span> : null}
        </p>
      </div>

      <div className={styles.field}>
        <label htmlFor={`context-${attribute.id}`} className={styles.label}>
          Context <span className={styles.optional}>optional</span>
        </label>
        <input
          id={`context-${attribute.id}`}
          className={styles.text}
          type="text"
          value={context}
          onChange={(event) => setContext(event.target.value)}
          disabled={submitting}
          placeholder="What this relates to — a review round, a project"
          maxLength={2000}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor={`rationale-${attribute.id}`} className={styles.label}>
          Rationale <span className={styles.optional}>optional</span>
        </label>
        <textarea
          id={`rationale-${attribute.id}`}
          className={styles.textarea}
          rows={3}
          value={rationale}
          onChange={(event) => setRationale(event.target.value)}
          disabled={submitting}
          placeholder="Your reasoning for this rating"
          maxLength={2000}
        />
        <p className={styles.help}>
          Your own reasoning. Kept separate from evidence, below — evidence is independently examinable; this is your
          opinion about what it means.
        </p>
      </div>

      <div className={styles.field}>
        <span className={styles.label}>
          Evidence <span className={styles.optional}>optional, up to {MAX_EVIDENCE_ITEMS}</span>
        </span>
        {evidenceItems.length === 0 ? (
          <p className={styles.help}>
            Something that supports this rating — a project outcome, an observed behaviour, a prior result. Not
            required: a rating with none is complete, not incomplete.
          </p>
        ) : (
          <ul className={styles.evidenceList}>
            {evidenceItems.map((item, index) => (
              <li key={item.key} className={styles.evidenceItem}>
                <div className={styles.evidenceRow}>
                  <label className={styles.evidenceFieldLabel} htmlFor={`evidence-type-${attribute.id}-${item.key}`}>
                    Type
                  </label>
                  <select
                    id={`evidence-type-${attribute.id}-${item.key}`}
                    className={styles.evidenceSelect}
                    value={item.evidenceTypeCode}
                    onChange={(event) => updateEvidenceItem(item.key, { evidenceTypeCode: event.target.value })}
                    disabled={submitting}
                    required
                  >
                    <option value="">Choose a type…</option>
                    {EVIDENCE_TYPE_OPTIONS.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className={styles.secondary}
                    onClick={() => removeEvidenceItem(item.key)}
                    disabled={submitting}
                    aria-label={`Remove evidence item ${index + 1}`}
                  >
                    Remove
                  </button>
                </div>

                <label className={styles.evidenceFieldLabel} htmlFor={`evidence-description-${attribute.id}-${item.key}`}>
                  Description
                </label>
                <textarea
                  id={`evidence-description-${attribute.id}-${item.key}`}
                  className={styles.textarea}
                  rows={2}
                  value={item.description}
                  onChange={(event) => updateEvidenceItem(item.key, { description: event.target.value })}
                  disabled={submitting}
                  placeholder="What this evidence is"
                  maxLength={2000}
                  required
                />

                <div className={styles.evidenceRow}>
                  <label className={styles.evidenceFieldLabel} htmlFor={`evidence-date-${attribute.id}-${item.key}`}>
                    When <span className={styles.optional}>optional</span>
                  </label>
                  <input
                    id={`evidence-date-${attribute.id}-${item.key}`}
                    className={styles.text}
                    type="date"
                    value={item.occurredOn}
                    onChange={(event) => updateEvidenceItem(item.key, { occurredOn: event.target.value })}
                    disabled={submitting}
                  />
                  <label
                    className={styles.evidenceFieldLabel}
                    htmlFor={`evidence-reference-${attribute.id}-${item.key}`}
                  >
                    Reference <span className={styles.optional}>optional</span>
                  </label>
                  <input
                    id={`evidence-reference-${attribute.id}-${item.key}`}
                    className={styles.text}
                    type="text"
                    value={item.externalReference}
                    onChange={(event) => updateEvidenceItem(item.key, { externalReference: event.target.value })}
                    disabled={submitting}
                    placeholder="A pointer to it — a document, a link"
                    maxLength={500}
                  />
                </div>

                <label className={styles.restrictedOption}>
                  <input
                    type="checkbox"
                    checked={item.restricted}
                    onChange={(event) => updateEvidenceItem(item.key, { restricted: event.target.checked })}
                    disabled={submitting}
                  />
                  Restrict this item
                </label>
                <p className={styles.help}>
                  Afterwards visible only to {employeeName}, and to admin/HR — not to anyone else who can otherwise
                  see this record, including you, unless you are one of those.
                </p>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          className={styles.secondary}
          onClick={addEvidenceItem}
          disabled={submitting || evidenceItems.length >= MAX_EVIDENCE_ITEMS}
        >
          Add evidence
        </button>
      </div>

      {state.status === 'failed' ? (
        <p className={styles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={styles.actions}>
        <button type="submit" className={styles.primary} disabled={submitting || !ratingIsValid || !evidenceIsValid}>
          {submitting ? 'Recording…' : correcting ? 'Record correction' : 'Record assessment'}
        </button>
        <button type="button" className={styles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>

      <p className={styles.note}>
        Assessments are never edited or deleted. A correction is always a new record that replaces an earlier one,
        and both are kept.
      </p>
    </form>
  );
}

/** Whether offering the form would be honest — used by the row above. */
export function canAssess(
  attribute: AttributeDefinitionResponse,
  viewerCanAssessAs: SourceTypeCode | null
): viewerCanAssessAs is SourceTypeCode {
  // Two facts, both from the API: the relationship the viewer stands in, and
  // the source types this attribute permits. Neither is inferred here, and
  // the write path re-checks both — this only decides whether offering the
  // affordance would be honest.
  return viewerCanAssessAs !== null && attribute.permittedSourceTypes.includes(viewerCanAssessAs);
}
